import { defaults, clone, isNumber, isObject, pick, noop } from 'lodash';
import { TweenMax, TimelineMax, Circ, Power1 } from 'gsap';

const Directions = { up: 'up', right: 'right', down: 'down', left: 'left' };

interface EnterAnimationParams {
  // the delay to do if something else is leaving
  delayIfLeaving: number;
  // the delay to take at all times
  delay: number;
  // the duration of the animation
  duration: number;
  // the easy to apply, defaults will be on a per animation basis
  ease: string;

  /** SLIDE PARAMS */
  // the distance it hsould move in case of a slide
  distance: number;
  // the direction it should slide
  direction: Directions;

  isChildTimeline: boolean;
}

function getComputedStyle(...args: EnterAnimationParams | any) {
  if (window) {
    return window.getComputedStyle(...args);
  }
  return {};
}

const HideNode = (
  node: any,
  timeline: TimelineMax = new TimelineMax(),
  delay: number,
  animationFunc?: () => TimelineMax = noop,
) => {
  timeline.delay(delay);
  TweenMax.set(node, { display: 'none' });
  timeline.add(() => {
    TweenMax.set(node, { display: '' });
    return animationFunc();
  }, 0);
  return timeline;
};

const ShouldDelayForLeave: (context: any, delay: number) => boolean = (context, delay) =>
  !!(isNumber(delay) && delay !== 0 && context && context.anyLeaving);

function checkDelay(node, context, params, timeline) {
  if (ShouldDelayForLeave(context, params.delayIfLeaving)) {
    HideNode(node, timeline, Math.max(params.delayIfLeaving, params.delay || 0));
  }
}

function convertArgsToParams(...args): EnterAnimationParams {
  // assume it's a param object if its' an object
  if (isObject(args[0])) {
    return clone(args[0]);
  }
  // now the params based on what's being passed in
  // on the layout of duration, delayIfLeaving, ease, delay,
  return {
    duration: args[0],
    delayIfLeaving: args[1],
    ease: args[2],
    delay: args[3],
  };
}

function getNodeProps(node: HTMLElement, ...props: string): { computed: Object, original: Object } {
  // first, save all the original props - they might have values
  // from a previous tween or have a style set in general
  const nodeStyle = pick(node.style, props);
  // now turn them off so we can get the natural values
  Object.assign(
    node.style,
    props.reduce((obj, value) => {
      obj[value] = null;
      return obj;
    }, {}),
  );

  // actually get the natural values out to know where we're going
  const computed = pick(getComputedStyle(node), props);
  // finally, restore the old props
  Object.assign(node.style, nodeStyle);

  return {
    computed,
    original: nodeStyle,
  };
}

function createAppearTween(node, params: EnterAnimationParams) {
  const { duration, ease } = params;

  // if the node we're bringing in already has an opacity set,
  // we should animate to -that- opacity, not 1
  const { original, computed } = getNodeProps(node, 'opacity');
  const { opacity } = computed;
  const finalOpacity = opacity ? parseInt(opacity, 10) : 1;

  return TweenMax.fromTo(
    node,
    duration,
    { css: { opacity: original.opacity || 0 } },
    {
      css: {
        opacity: finalOpacity,
        clearProps: 'opacity',
      },
      ease,
    },
  );
}

function createGrowVerticalTween(node, params: EnterAnimationParams) {
  const { duration, ease } = params;
  const growProps = [
    'height',
    'paddingTop',
    'paddingBottom',
    'marginTop',
    'marginBottom',
    'borderTopWidth',
    'borderBottomWidth',
  ];

  const { original, computed } = getNodeProps(node, ...growProps);

  const {
    height,
    paddingTop,
    paddingBottom,
    marginTop,
    marginBottom,
    borderTopWidth,
    borderBottomWidth,
  } = computed;

  return TweenMax.fromTo(
    node,
    duration,
    {
      css: {
        overflow: 'hidden',
        flexGrow: 0,
        height: original.height || 0,
        paddingTop: original.paddingTop || 0,
        paddingBottom: original.paddingBottom || 0,
        marginTop: original.marginTop || 0,
        marginBottom: original.marginBottom || 0,
        borderTopWidth: original.borderTopWidth || 0,
        borderBottomWidth: original.borderBottomWidth || 0,
      },
    },
    {
      css: {
        height,
        paddingTop,
        paddingBottom,
        marginTop,
        marginBottom,
        borderTopWidth,
        borderBottomWidth,
        clearProps:
          'flexGrow,overflow,height,paddingTop,paddingBottom,marginTop,marginBottom,borderTopWidth,borderBottomWidth',
      },
      ease: ease || Power1.easeInOut,
    },
  );
}

function createGrowHorizontalTween(node, params: EnterAnimationParams) {
  const { duration, ease } = params;
  const growProps = [
    'width',
    'paddingLeft',
    'paddingRight',
    'marginLeft',
    'marginRight',
    'borderLeftWidth',
    'borderRightWidth',
  ];
  const { original, computed } = getNodeProps(node, ...growProps);

  const {
    width,
    paddingLeft,
    paddingRight,
    marginLeft,
    marginRight,
    borderLeftWidth,
    borderRightWidth,
  } = computed;
  return TweenMax.fromTo(
    node,
    duration,
    {
      css: {
        overflow: 'hidden',
        width: original.width || 0,
        paddingLeft: original.paddingLeft || 0,
        paddingRight: original.paddingRight || 0,
        marginLeft: original.marginLeft || 0,
        marginRight: original.marginRight || 0,
        borderLeftWidth: original.borderLeftWidth || 0,
        borderRightWidth: original.borderRightWidth || 0,
      },
    },
    {
      css: {
        width,
        paddingLeft,
        paddingRight,
        marginLeft,
        marginRight,
        borderLeftWidth,
        borderRightWidth,
        clearProps:
          'overflow,width,paddingLeft,paddingRight,marginLeft,marginRight,borderLeftWidth,borderRightWidth',
      },
      ease,
    },
  );
}

/**
 * Generic Slide and Appear method, this is used by everything else
 */
export function slide(...args: EnterAnimationParams | any) {
  return (node, context) => {
    const params = defaults(convertArgsToParams(...args), {
      duration: 0.3,
      delayIfLeaving: 0.001,
      delay: 0,
      ease: Circ.easeOut,
      direction: Directions.right,
    });

    const {
      delay,
      delayIfLeaving,
      duration,
      distance,
      direction,
      ease,
      isChildTimeline,
      onComplete,
    } = params;

    const doAnimation = () => {
      const innerTimeline = new TimelineMax({ onComplete });
      // make sure this is done before we possibly hide it
      // because if it's hidden, it's got no width/height that we need!
      const { computed, original } = getNodeProps(node, 'height', 'width', 'x', 'y');
      const { height, width } = computed;
      // pick the correct dimension based on which way we're going
      const nodeSize = [Directions.down, Directions.up].includes(direction) ? height : width;
      const realDistance = isNumber(distance) ? distance : Number.parseInt(nodeSize, 10);

      const from = {};
      // for whatever reason, the 'clearProps' doesn't work if this a child timeline
      // but what does work is setting it for the onComplete of the -tween-
      // (not the timeline ,that doesn't work') why, I don't know.
      const to = {};
      switch (direction) {
        case Directions.up:
          from.y = realDistance;
          to.clearProps = 'y';
          to.y = original.y || 0;
          break;
        case Directions.down:
          from.y = realDistance * -1;
          to.y = original.y || 0;
          to.clearProps = 'y';
          break;
        case Directions.left:
          from.x = realDistance;
          to.x = original.x || 0;
          to.clearProps = 'x';
          break;
        case Directions.right:
        default:
          // assume 'right' if nothign given. But we should be given something
          from.x = realDistance * -1;
          to.x = original.x || 0;
          to.clearProps = 'x';
      }
      innerTimeline.add(
        TweenMax.fromTo(
          node,
          duration,
          { css: from },
          {
            css: to,
            ease,
          },
        ),
        0,
      );
      return innerTimeline;
    };

    if (!isChildTimeline && (ShouldDelayForLeave(context, delayIfLeaving) || delay)) {
      // small additional delay to try make sure the leaving element is gone
      const hideDelay = Math.max(delayIfLeaving, delay || 0) + 0.005;
      return HideNode(node, undefined, hideDelay, doAnimation);
    }

    return doAnimation();
  };
}

/** Animations the 'growing' of the element from nothing to it's full height */
export function grow(...args: EnterAnimationParams | any) {
  return (node: HTMLElement, context) => {
    const params: EnterAnimationParams = convertArgsToParams(...args);
    defaults(params, { duration: 0.3, ease: Power1.easeInOut, delay: 0 });

    const { delay, delayIfLeaving, isChildTimeline } = params;

    const timeline = new TimelineMax();

    if (!isChildTimeline && (ShouldDelayForLeave(context, delayIfLeaving) || delay)) {
      HideNode(node, timeline, Math.max(delayIfLeaving, delay || 0));
    }

    timeline.add(createGrowVerticalTween(node, params), 0);

    return timeline;
  };
}

export function appear(...args: EnterAnimationParams | any) {
  return (node, context) => {
    const params: EnterAnimationParams = convertArgsToParams(...args);
    defaults(params, { duration: 0.3, delay: 0, delayIfLeaving: 0.001 });

    const { delay, delayIfLeaving, isChildTimeline } = params;

    const timeline = new TimelineMax();

    if (!isChildTimeline && (ShouldDelayForLeave(context, delayIfLeaving) || delay)) {
      HideNode(node, timeline, Math.max(delayIfLeaving, delay || 0));
    }

    timeline.add(createAppearTween(node, params), 0);

    return timeline;
  };
}

export function growHorizontally(...args: EnterAnimationParams | any) {
  return (node, context) => {
    const params: EnterAnimationParams = convertArgsToParams(...args);
    defaults(params, { duration: 0.3, ease: Power1.easeInOut });

    const { delay, delayIfLeaving, isChildTimeline } = params;
    const timeline = new TimelineMax({ delay });

    if (!isChildTimeline && (ShouldDelayForLeave(context, delayIfLeaving) || delay)) {
      HideNode(node, timeline, delayIfLeaving);
    }

    timeline.add(createGrowHorizontalTween(node, params), 0);

    return timeline;
  };
}

// Grow the element in a circular fashion to it's full height
export function growCircular(...args: EnterAnimationParams | any) {
  const params = defaults(convertArgsToParams(...args), {
    duration: 0.3,
    delay: 0,
    ease: Circ.easeOut,
  });

  return (node, context) => {
    const { delay, delayIfLeaving, duration, ease } = params;
    const { original, computed } = getNodeProps(node, 'scale', 'height', 'width');

    const timeline = new TimelineMax();

    if (ShouldDelayForLeave(context, delayIfLeaving) || delay) {
      HideNode(node, timeline, Math.max(delayIfLeaving, delay || 0));
    }

    const radius = Math.hypot(parseInt(computed.height, 10), parseInt(computed.width, 10));
    timeline.add(
      TweenMax.fromTo(
        node,
        0.5,
        { width: 0, height: 0, x: 0, y: 0, borderRadius: '100%' },
        {
          width: radius * 2,
          height: radius * 2,
          x: -radius,
          y: -radius,
          ease,
          clearProps: 'width,height,x,y,borderRadius',
        },
      ),
      0,
    );

    return timeline;
  };
}

export function scaleUp(...args: EnterAnimationParams | any) {
  const params = defaults(convertArgsToParams(...args), { duration: 0.3, delay: 0 });
  return (node, context) => {
    const { delay, delayIfLeaving, duration, ease, isChildTimeline } = params;
    const { original } = getNodeProps(node, 'scale');
    const timeline = new TimelineMax();
    timeline.add(TweenMax.fromTo(
      node,
      duration,
      { css: { scale: original.scale || 0 } },
      { css: { scale: 1, clearProps: 'scale' }, ease },
    ));

    if (!isChildTimeline && (ShouldDelayForLeave(context, delayIfLeaving) || delay)) {
      HideNode(node, timeline, Math.max(delayIfLeaving, delay || 0));
    }

    return timeline;
  };
}

export function slideUp(...args: EnterAnimationParams | any) {
  const params = convertArgsToParams(...args);
  params.direction = Directions.up;
  return slide(params);
}

export function slideDown(...args: EnterAnimationParams | any) {
  const params = convertArgsToParams(...args);
  params.direction = Directions.down;
  return slide(params);
}

export function slideLeft(...args: EnterAnimationParams | any) {
  const params = convertArgsToParams(...args);
  params.direction = Directions.left;
  return slide(params);
}

export function slideRight(...args: EnterAnimationParams | any) {
  const params = convertArgsToParams(...args);
  params.direction = Directions.right;
  return slide(params);
}

/**
 * Will slide the node into it's final position based on the direction given in the params
 * Will also make the node 'appear' at the same time as it's slide.
 */
export function slideAndAppear(...args: EnterAnimationParams | any) {
  const params = defaults(convertArgsToParams(...args), { duration: 0.3, delay: 0 });
  params.isChildTimeline = true;

  return (node, context) => {
    const { delayIfLeaving, delay, ...otherProps } = params;
    const timeline = new TimelineMax();

    timeline.add(slide(otherProps)(node, context), 0);
    timeline.add(createAppearTween(node, params), 0);

    if (ShouldDelayForLeave(context, delayIfLeaving) || delay) {
      HideNode(node, timeline, Math.max(delayIfLeaving, delay || 0));
    }

    return timeline;
  };
}

/** Slide the node from the right hand side into it's final position, i.e. it's moving left  */
export function slideLeftAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3, delay: 0 });

  params.direction = Directions.left;

  return slideAndAppear(params);
}

/** Slide the node from the left hand side into it's final position, i.e. it's moving right */
export function slideRightAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3, delay: 0 });

  params.direction = Directions.right;

  return slideAndAppear(params);
}

/** Slide the node in question from the bottom into it's final position, i.e. it's moving upwards */
export function slideUpAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3, delay: 0 });

  params.direction = Directions.up;

  return slideAndAppear(params);
}

/** Slide the node in question from the top into position, i.e. it's moving downwards */
export function slideDownAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  params.direction = Directions.down;

  return slideAndAppear(params);
}

/**
 * Slide and appear the children in the horizontal plane
 * Use first param to go left or right. This is meant to be a helper
 * For when you want to swap the direction easy without calling
 * the seperate slides
 */
export function horizontalSlideAndAppear(
  goRight: boolean = true,
  ...args: EnterAnimationParams | any
) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  params.direction = goRight ? Directions.right : Directions.left;

  return slideAndAppear(params);
}

export function slideLeftAndGrow(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.direction = Directions.left;
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(createGrowHorizontalTween(node, params), 0);
    timeline.add(slide(params)(node, context), 0);
    checkDelay(node, context, params, timeline);

    return timeline;
  };
}

export function slideRightAndGrow(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.direction = Directions.right;
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(createGrowHorizontalTween(node, params), 0);
    timeline.add(slide(params)(node, context), 0);

    checkDelay(node, context, params, timeline);

    return timeline;
  };
}

export function slideUpAndGrow(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.direction = Directions.up;
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(createGrowVerticalTween(node, params), 0);
    timeline.add(slide(params)(node, context), 0);
    checkDelay(node, context, params, timeline);

    return timeline;
  };
}

export function slideDownAndGrow(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.direction = Directions.down;
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();
    timeline.add(createGrowVerticalTween(node, params), 0);
    timeline.add(slide(params)(node, context), 0);
    checkDelay(node, context, params, timeline);

    return timeline;
  };
}

export function slideGrowAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();

    timeline.add(createAppearTween(node, params), 0);
    timeline.add(slide(params)(node, context), params.duration);
    timeline.add(createGrowVerticalTween(node, params), params.duration);
    checkDelay(node, context, params, timeline);
    return timeline;
  };
}

export function growAndAppear(...args: EnterAnimationParams | any) {
  const params: EnterAnimationParams = convertArgsToParams(...args);
  defaults(params, { duration: 0.3 });
  params.isChildTimeline = true;

  return (node, context) => {
    const timeline = new TimelineMax();

    timeline.add(createAppearTween(node, params), 0);
    timeline.add(createGrowVerticalTween(node, params), 0);
    checkDelay(node, context, params, timeline);
    return timeline;
  };
}
